import { FieldErrors, FieldError, Merge, FieldErrorsImpl } from "react-hook-form";
import { FormErrorData } from "src/types/analytics";

const ADDRESS_FIELD_KEYS = new Set(["currentAddress", "previousAddress"]);
const DOB_FIELD_KEYS = new Set(["dobDay", "dobMonth", "dobYear", "dobCombined"]);
// flat number / house number / house name are a single "either/or" requirement in the
// schema, so when the building is missing they error together. When more than one errors at
// once we collapse them into a single "building" error to avoid multi-counting the same
// underlying problem. A lone building identifier error keeps its specific field name.
const BUILDING_IDENTIFIER_KEYS = new Set(["flatNumber", "houseNumber", "houseName"]);

const getErrorMessage = (
    error: FieldError | Merge<FieldError, FieldErrorsImpl<Record<string, unknown>>> | undefined
): string => {
    return typeof error?.message === "string" ? error.message.toLowerCase() : "";
};

const determineErrorType = (errorMessage: string): "no entry" | "invalid entry" => {
    return errorMessage.includes("required") ? "no entry" : "invalid entry";
};

export const parseFormErrors = (errors: FieldErrors): FormErrorData => {
    const fieldErrors: FormErrorData = [];

    Object.keys(errors).forEach((key) => {
        const error = errors[key];

        if (DOB_FIELD_KEYS.has(key)) {
            if (!fieldErrors.some((e) => e.fieldName === "date of birth")) {
                const errorMessage = getErrorMessage(error);
                const errorType = determineErrorType(errorMessage);
                fieldErrors.push({ fieldName: "date of birth", errorType });
            }
        } else if (ADDRESS_FIELD_KEYS.has(key)) {
            const addressPrefix = key === "currentAddress" ? "current address" : "previous address";
            const nestedErrors = error as FieldErrors;
            const nestedKeys = Object.keys(nestedErrors);
            // Only collapse when more than one building identifier errors at once. That is the
            // mutual "either/or required" over-count scenario. A lone building identifier error
            // (e.g. an invalid character in just the flat number) keeps its specific field name.
            const shouldCollapseBuildingIdentifiers =
                nestedKeys.filter((nestedKey) => BUILDING_IDENTIFIER_KEYS.has(nestedKey)).length > 1;
            let buildingIdentifierErrorAdded = false;
            nestedKeys.forEach((nestedKey) => {
                const nestedError = nestedErrors[nestedKey];
                const errorMessage = getErrorMessage(nestedError);
                const errorType = determineErrorType(errorMessage);

                if (shouldCollapseBuildingIdentifiers && BUILDING_IDENTIFIER_KEYS.has(nestedKey)) {
                    if (buildingIdentifierErrorAdded) {
                        return;
                    }
                    buildingIdentifierErrorAdded = true;
                    fieldErrors.push({ fieldName: `${addressPrefix} building`, errorType });
                } else {
                    fieldErrors.push({ fieldName: `${addressPrefix} ${nestedKey}`, errorType });
                }
            });
        } else {
            const errorMessage = getErrorMessage(error);
            const errorType = determineErrorType(errorMessage);
            fieldErrors.push({ fieldName: key, errorType });
        }
    });

    return fieldErrors;
};
